import gsap from 'gsap';
import * as THREE from 'three';

export function initThreeScene({ particlesCount = 100 } = {}) {
    const parameters = {
        materialColor: '#FFFFFF'
    };

    // Canvas
    const canvas = document.querySelector('canvas.webgl');

    // Scene
    const scene = new THREE.Scene();

    // Textures
    const textureLoader = new THREE.TextureLoader();

    // Texturas de la luna y fondo estelar (NASA)
    const textureURL = "https://s3-us-west-2.amazonaws.com/s.cdpn.io/17271/lroc_color_poles_1k.jpg";
    const displacementURL = "https://s3-us-west-2.amazonaws.com/s.cdpn.io/17271/ldem_3_8bit.jpg";

    textureLoader.setCrossOrigin?.('anonymous');

    const moonColor = textureLoader.load(textureURL, t => {
        t.colorSpace = THREE.SRGBColorSpace;
    });
    const moonDisp = textureLoader.load(displacementURL);

    const gradientTexture = textureLoader.load('/textures/gradients/3.jpg');

    const asteroideColorBase = textureLoader.load('/textures/Rock_044_BaseColor.jpg');
    asteroideColorBase.colorSpace = THREE.SRGBColorSpace;
    asteroideColorBase.repeat.set(5, 5);
    asteroideColorBase.wrapS = THREE.RepeatWrapping;
    asteroideColorBase.wrapT = THREE.RepeatWrapping;

    const asteroideNormalMap = textureLoader.load('/textures/Rock_044_Normal.jpg');
    const asteroideRoughnessMap = textureLoader.load('/textures/Rock_044_Roughness.jpg');
    const asteroideAoMap = textureLoader.load('/textures/Rock_044_AmbientOcclusion.jpg');
    const asteroideHeightMap = textureLoader.load('/textures/Rock_044_Height.jpg');

    gradientTexture.magFilter = THREE.NearestFilter;

    // Materials
    const material = new THREE.MeshStandardMaterial({
        map: asteroideColorBase,
        normalMap: asteroideNormalMap,
        roughnessMap: asteroideRoughnessMap,
        aoMap: asteroideAoMap,
        displacementMap: asteroideHeightMap,
        displacementScale: 0.2,
        aoMapIntensity: 1,
        metalness: 0.2
    });


    const moonMaterial = new THREE.MeshPhongMaterial(
        {
            color: 0xffffff,
            map: moonColor,
            displacementMap: moonDisp,
            displacementScale: 0.05,
            bumpMap: moonDisp,
            bumpScale: 1,
            reflectivity: 0,
            shininess: 0
        }

    );

    // Meshes
    let objectsDistance = 4;
    let isMobile = window.innerWidth < 800;
    if (isMobile) {
        objectsDistance = 3;
    }

    //const moonGeo = new THREE.Mesh(geometry, material);
    const geometry1 = new THREE.SphereGeometry(1, 80, 80);
    const mesh1 = new THREE.Mesh(geometry1, moonMaterial);

    const mesh2 = new THREE.Mesh(
        new THREE.DodecahedronGeometry(1, 3),
        material
    );
    const mesh3 = new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        material
    );

    material.side = THREE.DoubleSide;
    moonMaterial.side = THREE.DoubleSide;

    mesh1.position.y = -objectsDistance * 0;
    mesh2.position.y = -objectsDistance * 1;
    mesh3.position.y = -objectsDistance * 2;

    mesh1.position.x = 2;
    mesh2.position.x = -2;
    mesh3.position.x = 2;

    scene.add(mesh1, mesh2, mesh3);


    const sectionMeshes = [mesh1, mesh2, mesh3];
    if (isMobile) {
        sectionMeshes.forEach((mesh, index) => {
            mesh.scale.set(0.8, 0.8, 0.8);
            mesh.position.x = index % 2 === 0 ? 1 : -1;
        });
    }

    // Particles
    const positions = new Float32Array(particlesCount * 3);
    for (let i = 0; i < particlesCount; i++) {
        positions[i * 3 + 0] = (Math.random() - 0.5) * 10;
        positions[i * 3 + 1] = objectsDistance * 0.5 - Math.random() * objectsDistance * sectionMeshes.length;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }

    const particlesGeometry = new THREE.BufferGeometry();
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const particlesMaterial = new THREE.PointsMaterial({
        color: parameters.materialColor,
        sizeAttenuation: true,
        size: 0.02,
        transparent: true
    });

    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);

    // Lights
    const ambientLight = new THREE.AmbientLight('#0a0f25', 0.5);
    scene.add(ambientLight);

    const directLight = new THREE.DirectionalLight('#ffffff', 3);
    directLight.position.set(2.4, 1.1, 3.6);
    scene.add(directLight);

    // ambientLight.intensity = 0.15;           // antes 0.5
    // directLight.intensity = 1.6;             // antes 5 (demasiado)
    // directLight.position.set(2.4, 1.1, 1.6); // ángulo oblicuo = terminador bonito

    // Sizes
    const sizes = {
        width: window.innerWidth,
        height: window.innerHeight
    };

    window.addEventListener('resize', () => {
        sizes.width = window.innerWidth;
        sizes.height = window.innerHeight;

        isMobile = sizes.width < 800;
        objectsDistance = isMobile ? 3 : 4;
        sectionMeshes.forEach((mesh, index) => {
            mesh.scale.set(isMobile ? 0.8 : 1, isMobile ? 0.8 : 1, isMobile ? 0.8 : 1);
            mesh.position.y = -objectsDistance * index;
            mesh.position.x = isMobile
                ? index % 2 === 0 ? 1 : -1
                : index % 2 === 0 ? 2 : -2;
        });

        camera.position.z = isMobile ? 7 : 6;

        camera.aspect = sizes.width / sizes.height;
        camera.updateProjectionMatrix();

        renderer.setSize(sizes.width, sizes.height);

        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    });

    // Camera
    const cameraGroup = new THREE.Group();
    scene.add(cameraGroup);

    const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
    camera.position.z = isMobile ? 7 : 6;
    cameraGroup.add(camera);

    // Renderer
    const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        alpha: true
    });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Scroll
    let scrollY = window.scrollY;
    let currentSection = 0;

    window.addEventListener('scroll', () => {
        scrollY = window.scrollY;
        const newSection = Math.round(scrollY / sizes.height);
        const clampedSection = Math.min(newSection, sectionMeshes.length - 1);
        if (clampedSection !== currentSection) {
            currentSection = clampedSection;
            gsap.to(sectionMeshes[currentSection].rotation, {
                duration: 1.5,
                ease: 'power2.inOut',
                x: '+=6',
                y: '+=3',
                z: '+=1.5'
            });
        }
    });

    // Cursor
    const cursor = { x: 0, y: 0 };
    window.addEventListener('mousemove', (event) => {
        cursor.x = event.clientX / sizes.width - 0.5;
        cursor.y = event.clientY / sizes.height - 0.5;
    });

    // Animate
    const clock = new THREE.Clock();
    let previousTime = 0;

    const tick = () => {
        const elapsedTime = clock.getElapsedTime();
        const deltaTime = elapsedTime - previousTime;
        previousTime = elapsedTime;

        camera.position.y = -scrollY / sizes.height * objectsDistance;

        const parallaxX = cursor.x;
        const parallaxY = -cursor.y;
        cameraGroup.position.x += (parallaxX - cameraGroup.position.x) * 5 * deltaTime;
        cameraGroup.position.y += (parallaxY - cameraGroup.position.y) * 5 * deltaTime;

        for (const mesh of sectionMeshes) {
            mesh.rotation.x += deltaTime * 0.1;
            mesh.rotation.y += deltaTime * 0.12;
        }
        particlesMaterial.opacity = 0.75 + 0.25 * Math.sin(elapsedTime * 3);

        renderer.render(scene, camera);
        window.requestAnimationFrame(tick);
    };

    tick();
}

